/**
 * Offline Mode Type Definitions
 *
 * Type-safe interfaces for offline operations, sync queue, and IndexedDB entities.
 * Follows the project's strict TypeScript standards (no `any` types).
 */

import type { Table } from 'dexie';
import type { PaginatedResponse } from '#/api';

// ============================================
// ENTITY TYPE ENUM
// ============================================

/**
 * All entity types that support offline operations
 */
export type EntityType =
  | 'transaction'
  | 'contact'
  | 'payable'
  | 'receivable'
  | 'inventory'
  | 'employee'
  | 'branch'
  | 'transactionCategory'
  | 'compositeItem';

/**
 * Operation types for sync queue
 */
export type OperationType = 'create' | 'update' | 'delete';

/**
 * Sync status for individual items
 */
export type SyncStatus = 'synced' | 'pending' | 'syncing' | 'failed';

// ============================================
// OFFLINE ENTITY MARKERS
// ============================================

/**
 * Marker interface for entities stored offline
 * Added to all cached entities to track offline state
 */
export interface OfflineMetadata {
  /** When this entity was cached locally */
  _offlineCachedAt: number;
  /** Whether this entity has been synced to server */
  _offlineSynced: boolean;
  /** Version number for conflict detection */
  _offlineVersion: number;
  /** True if created offline (has temporary ID) */
  _isTemporary?: boolean;
  /** Client timestamp for last-write-wins conflict resolution */
  _clientTimestamp: number;
}

/**
 * Entity with offline metadata
 */
export type OfflineEntity<T> = T & OfflineMetadata;

// ============================================
// SYNC QUEUE TYPES
// ============================================

/**
 * Item in the sync queue
 * Represents a pending operation to be synced to the server
 */
export interface SyncQueueItem {
  /** Unique ID for this queue item */
  id: string;
  /** Type of entity being synced */
  entityType: EntityType;
  /** Operation to perform */
  operation: OperationType;
  /** Entity ID (temporary for creates, server ID for updates/deletes) */
  entityId: string;
  /** The data payload for the operation */
  payload: Record<string, unknown>;
  /** Current sync status */
  status: 'pending' | 'syncing' | 'failed';
  /** Number of retry attempts */
  retryCount: number;
  /** Maximum retries before marking as failed */
  maxRetries: number;
  /** When this item was queued */
  createdAt: number;
  /** When last sync attempt was made */
  lastAttemptAt: number | null;
  /** Error message if failed */
  errorMessage: string | null;
  /** Client timestamp for conflict resolution */
  clientTimestamp: number;
}

/**
 * Metadata for tracking sync state per entity type
 */
export interface SyncMetadata {
  /** Entity type this metadata is for */
  entityType: EntityType;
  /** When this entity type was last synced from server */
  lastSyncedAt: number | null;
  /** Sync version for incremental sync support */
  syncVersion: number;
  /** Current sync status */
  status: 'idle' | 'syncing' | 'error';
  /** Error message if status is 'error' */
  errorMessage?: string;
}

// ============================================
// SYNC RESULT TYPES
// ============================================

/**
 * Result of a sync operation
 */
export interface SyncResult<T> {
  /** Whether the sync succeeded */
  success: boolean;
  /** The synced data (if successful) */
  data?: T;
  /** Error message (if failed) */
  error?: string;
  /** Whether a conflict was resolved */
  conflictResolved?: boolean;
  /** The new server ID (for creates with temp IDs) */
  serverId?: string;
}

/**
 * Result of processing the entire sync queue
 */
export interface SyncQueueResult {
  /** Total items processed */
  totalProcessed: number;
  /** Successfully synced items */
  successCount: number;
  /** Failed items */
  failedCount: number;
  /** Items that had conflicts resolved */
  conflictsResolved: number;
  /** Details of failed items */
  failures: Array<{
    queueItemId: string;
    entityType: EntityType;
    error: string;
  }>;
}

// ============================================
// OFFLINE SERVICE CONFIGURATION
// ============================================

/**
 * API service methods interface
 * Defines the shape of API service methods that offline services wrap
 */
export interface ApiServiceMethods<TEntity, TCreate, TUpdate> {
  getAll: (filters?: Record<string, unknown>) => Promise<TEntity[] | PaginatedResponse<TEntity>>;
  getOne: (id: string) => Promise<TEntity>;
  create: (data: TCreate) => Promise<TEntity>;
  update: (id: string, data: TUpdate) => Promise<TEntity>;
  delete: (id: string) => Promise<void>;
}

/**
 * Configuration for an offline service
 */
export interface OfflineServiceConfig<TEntity, TCreate, TUpdate> {
  /** Entity type identifier */
  entityType: EntityType;
  /** IndexedDB table name */
  tableName: string;
  /** API endpoint base path */
  apiEndpoint: string;
  /** React Query key base */
  queryKeyBase: readonly string[];
  /** API service methods */
  apiService: ApiServiceMethods<TEntity, TCreate, TUpdate>;
  /** Transform entity before caching (optional) */
  transformForCache?: (entity: TEntity) => TEntity;
  /** Transform cached entity before returning (optional) */
  transformFromCache?: (entity: OfflineEntity<TEntity>) => TEntity;
}

// ============================================
// SYNC STORE TYPES
// ============================================

/**
 * Per-entity sync status
 */
export interface EntitySyncStatus {
  /** Number of pending items */
  pendingCount: number;
  /** Number of failed items */
  failedCount: number;
  /** When this entity was last synced */
  lastSyncAt: number | null;
  /** Current sync status */
  status: 'idle' | 'syncing' | 'error';
}

/**
 * Global sync state
 */
export interface SyncState {
  /** Whether the device is online */
  isOnline: boolean;
  /** Whether a sync is in progress */
  isSyncing: boolean;
  /** Total pending items across all entities */
  pendingCount: number;
  /** Total failed items across all entities */
  failedCount: number;
  /** When the last successful sync completed */
  lastSyncAt: number | null;
  /** Sync progress (0-100) */
  syncProgress: number;
  /** Per-entity sync status */
  entityStatus: Partial<Record<EntityType, EntitySyncStatus>>;
}

/**
 * Sync store actions
 */
export interface SyncActions {
  /** Update online status */
  setOnline: (isOnline: boolean) => void;
  /** Mark sync as started */
  startSync: () => void;
  /** Mark sync as completed */
  endSync: (success: boolean) => void;
  /** Update sync progress */
  updateProgress: (progress: number) => void;
  /** Update status for a specific entity */
  updateEntityStatus: (entity: EntityType, status: Partial<EntitySyncStatus>) => void;
  /** Increment pending count for entity */
  incrementPending: (entity: EntityType) => void;
  /** Decrement pending count for entity */
  decrementPending: (entity: EntityType) => void;
  /** Increment failed count for entity */
  incrementFailed: (entity: EntityType) => void;
  /** Reset failed count for entity */
  resetFailed: (entity: EntityType) => void;
  /** Reset all sync state */
  reset: () => void;
}

/**
 * Complete sync store interface
 */
export interface SyncStore extends SyncState {
  actions: SyncActions;
}

// ============================================
// ID MAPPING TYPES
// ============================================

/**
 * Maps temporary IDs to server IDs after sync
 */
export interface IdMapping {
  /** Temporary ID generated offline */
  tempId: string;
  /** Server-assigned ID after sync */
  serverId: string;
  /** Entity type */
  entityType: EntityType;
  /** When this mapping was created */
  createdAt: number;
}

// ============================================
// HELPER TYPES
// ============================================

/**
 * Generic table type for Dexie
 */
export type OfflineTable<T> = Table<OfflineEntity<T>, string>;

/**
 * Function type for generating temporary IDs
 */
export type TempIdGenerator = () => string;

/**
 * Filter function type for querying cached data
 */
export type CacheFilter<T> = (entity: OfflineEntity<T>) => boolean;

/**
 * Conflict resolver function type
 */
export type ConflictResolver<T> = (
  clientData: OfflineEntity<T>,
  serverData: T
) => T;

// ============================================
// CONSTANTS
// ============================================

/**
 * Prefix for temporary IDs
 * Used to identify entities created offline
 */
export const TEMP_ID_PREFIX = 'temp_';

/**
 * Default max retries for sync operations
 */
export const DEFAULT_MAX_RETRIES = 3;

/**
 * Default sync batch size
 */
export const DEFAULT_BATCH_SIZE = 10;

/**
 * Helper to check if an ID is temporary
 */
export function isTemporaryId(id: string): boolean {
  return id.startsWith(TEMP_ID_PREFIX);
}

/**
 * Generate a temporary ID
 */
export function generateTempId(): string {
  return `${TEMP_ID_PREFIX}${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}
